import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useSocket } from '../../context/SocketContext';
import '../Overlays/Overlays.css';

const SYSTEM_LOGO_MANIFEST_URL = '/system-logos/manifest.json';
const DEFAULT_SYSTEM_LOGOS = [
  {
    id: '00-suitestream-tv-default.png',
    label: 'Suitestream TV Default',
    url: '/system-logos/00-suitestream-tv-default.png',
  },
];

function normalizeOverlayType(value) {
  return String(value || '').toLowerCase();
}

function isSystemLogoUrl(value) {
  return String(value || '').startsWith('/system-logos/');
}

function WatermarkSettings({ site, currentDevice, subscriptionType = 'free' }) {
  const token = localStorage.getItem('token');
  const socket = useSocket();
  const commandIdRef = useRef(1);
  const normalizedSubscriptionType = String(subscriptionType || 'free').trim().toLowerCase();
  const customWatermarkAllowed = normalizedSubscriptionType !== 'free';
  const watermarkIsSystemEnforced = normalizedSubscriptionType === 'free';

  const [watermarkOverlay, setWatermarkOverlay] = useState(null);
  const [systemLogos, setSystemLogos] = useState(DEFAULT_SYSTEM_LOGOS);
  const [enabled, setEnabled] = useState(true);
  const [mode, setMode] = useState('system'); // system | custom
  const [displayMode, setDisplayMode] = useState('suitestream'); // suitestream | system
  const [selectedSystemLogoId, setSelectedSystemLogoId] = useState(DEFAULT_SYSTEM_LOGOS[0].id);
  const [customLogos, setCustomLogos] = useState([]);
  const [selectedCustomLogo, setSelectedCustomLogo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const selectedSystemLogo = useMemo(() => {
    return systemLogos.find((logo) => logo.id === selectedSystemLogoId) || systemLogos[0] || null;
  }, [selectedSystemLogoId, systemLogos]);

  const previewUrl = useMemo(() => {
    if (!enabled) return null;
    if (mode === 'system') return selectedSystemLogo?.url || null;
    return selectedCustomLogo?.preview_api_url || selectedCustomLogo?.thumbnail_url || null;
  }, [enabled, mode, selectedSystemLogo, selectedCustomLogo]);

  const sendCommand = (command, value = null) => {
    if (!socket || !currentDevice?.id) return;
    socket.emit('sendCommand', {
      deviceId: currentDevice.id,
      command,
      value,
      id: commandIdRef.current++,
    });
  };

  const applyToDevice = (nextState) => {
    if (!currentDevice?.id) return;
    if (!socket) return;
    const command = nextState.displayMode === 'system' ? 'setSystemWatermark' : 'setWatermark';
    const transparency =
      typeof nextState.transparency === 'number' && !Number.isNaN(nextState.transparency)
        ? nextState.transparency
        : 100;

    if (!nextState.enabled) {
      sendCommand(command, { enabled: false, transparency });
      return;
    }

    if (nextState.mode === 'system') {
      sendCommand(command, {
        enabled: true,
        image_url: nextState.systemLogoUrl,
        image_asset_id: null,
        image_asset_kind: null,
        transparency,
      });
      return;
    }

    sendCommand(command, {
      enabled: true,
      image_url: null,
      image_asset_id: nextState.customAssetId,
      image_asset_kind: nextState.customAssetKind || 'original',
      transparency,
    });
  };

  useEffect(() => {
    let ignore = false;

    const loadSystemLogos = async () => {
      try {
        const response = await axios.get(SYSTEM_LOGO_MANIFEST_URL);
        const logos = response.data?.logos || [];
        if (!ignore) {
          setSystemLogos(logos);
          setSelectedSystemLogoId((currentId) => {
            if (logos.some((logo) => logo.id === currentId)) return currentId;
            return logos[0]?.id || '';
          });
        }
      } catch (err) {
        if (!ignore) console.error('Failed to load system logo manifest', err);
      }
    };

    loadSystemLogos();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (!site?.id || !token) return;
    let ignore = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const [overlaysRes, logosRes] = await Promise.all([
          axios.get('/api/overlays', {
            params: { siteId: site.id },
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`/api/sites/${site.id}/logos`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if (ignore) return;

        const overlays = overlaysRes.data?.overlays || [];
        const watermarkOverlays = overlays.filter((o) => normalizeOverlayType(o.type) === 'watermark');
        watermarkOverlays.sort((a, b) => Number(b.id || 0) - Number(a.id || 0));
        const existing = watermarkOverlays[0] || null;
        setWatermarkOverlay(existing);

        const overlayEnabled = watermarkIsSystemEnforced
          ? true
          : existing
            ? !!(existing?.image_url || existing?.image_asset_id)
            : true;
        setEnabled(overlayEnabled);

        if (customWatermarkAllowed && existing?.image_asset_id) {
          setMode('custom');
          setSelectedCustomLogo(null);
        } else {
          setMode('system');
          const match =
            systemLogos.find((logo) => logo.url === existing?.image_url)
            || (watermarkIsSystemEnforced && !isSystemLogoUrl(existing?.image_url) ? systemLogos[0] : null)
            || systemLogos[0]
            || null;
          if (match?.id) {
            setSelectedSystemLogoId(match.id);
          }
        }

        const logos = logosRes.data?.logos || [];
        setCustomLogos(logos);
        if (customWatermarkAllowed && existing?.image_asset_id) {
          const chosen =
            logos.find((logo) => Number(logo.id) === Number(existing.image_asset_id)) || null;
          setSelectedCustomLogo(chosen);
        } else if (!selectedCustomLogo && logos.length) {
          setSelectedCustomLogo(logos[0]);
        }
      } catch (err) {
        if (!ignore) console.error('Failed to load watermark settings', err);
      } finally {
        if (!ignore) setIsLoading(false);
      }
    };

    load();

    return () => {
      ignore = true;
    };
  }, [site?.id, token, systemLogos, customWatermarkAllowed, watermarkIsSystemEnforced]);

  const buildPayload = () => {
    const base = {
      name: 'Watermark',
      type: 'watermark',
      message: null,
      qr_code_url: null,
      duration: 0,
      transparency: 100,
      site_id: site.id,
      campaign_id: null,
    };

    if (watermarkIsSystemEnforced) {
      return {
        ...base,
        image_url: selectedSystemLogo?.url || DEFAULT_SYSTEM_LOGOS[0].url,
        image_asset_id: null,
        image_asset_kind: null,
      };
    }

    if (!enabled) {
      return {
        ...base,
        image_url: null,
        image_asset_id: null,
        image_asset_kind: null,
      };
    }

    if (mode === 'system') {
      return {
        ...base,
        image_url: selectedSystemLogo?.url || null,
        image_asset_id: null,
        image_asset_kind: null,
      };
    }

    return {
      ...base,
      image_url: null,
      image_asset_id: selectedCustomLogo?.id || null,
      image_asset_kind: 'original',
    };
  };

  const handleSave = async () => {
    if (!site?.id || !token) return;
    if ((watermarkIsSystemEnforced || enabled) && mode === 'system' && !selectedSystemLogo?.url) {
      alert('Add at least one image to the system logo folder before saving.');
      return;
    }
    if (enabled && mode === 'custom' && !selectedCustomLogo?.id) {
      alert('Upload and select a custom logo first, or switch to System Logo.');
      return;
    }

    setIsSaving(true);
    try {
      const payload = buildPayload();
      if (watermarkOverlay?.id) {
        await axios.put(`/api/overlays/${watermarkOverlay.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        const res = await axios.post('/api/overlays', payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setWatermarkOverlay(res.data?.overlay || null);
      }

      applyToDevice({
        enabled: watermarkIsSystemEnforced ? true : enabled,
        mode: watermarkIsSystemEnforced ? 'system' : mode,
        displayMode,
        systemLogoUrl: selectedSystemLogo?.url || null,
        customAssetId: customWatermarkAllowed ? selectedCustomLogo?.id || null : null,
        customAssetKind: customWatermarkAllowed ? 'original' : null,
        transparency: payload.transparency,
      });

      alert('Watermark saved.');
    } catch (err) {
      console.error('Failed to save watermark', err);
      alert('Failed to save watermark.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <h3 style={{ marginTop: 20, marginBottom: 10, color: '#333' }}>Watermark</h3>
      <p style={{ marginTop: 0, maxWidth: 860 }}>
        One persistent watermark per site: choose a System Logo or a Custom Logo, or disable it entirely.
      </p>
      {watermarkIsSystemEnforced ? (
        <p style={{ marginTop: 0, maxWidth: 860, color: '#666', fontSize: 13 }}>
          Free accounts always show a System Logo watermark. Basic and higher plans can disable the
          watermark or replace it with a Custom Logo.
        </p>
      ) : null}
      <p style={{ marginTop: 0, maxWidth: 860, color: '#666', fontSize: 13 }}>
        System Logo choices are loaded from the webserver&apos;s `webserver/system-logos/` folder when
        the container starts. Add an image there and restart the `webserver` container to make it
        selectable here.
      </p>

      {isLoading ? (
        <p>Loading watermark settings...</p>
      ) : (
        <>
          <label style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              disabled={watermarkIsSystemEnforced}
            />
            {watermarkIsSystemEnforced ? 'Enabled (required on Free)' : 'Enabled'}
          </label>

          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ minWidth: 320, flex: '1 1 420px' }}>
              <div style={{ marginBottom: 10, fontWeight: 600 }}>Display Mode</div>
              <div style={{ display: 'flex', gap: 18, marginBottom: 12 }}>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="radio"
                    name="watermarkDisplayMode"
                    value="suitestream"
                    checked={displayMode === 'suitestream'}
                    onChange={() => setDisplayMode('suitestream')}
                    disabled={!enabled}
                  />
                  Suitestream
                </label>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="radio"
                    name="watermarkDisplayMode"
                    value="system"
                    checked={displayMode === 'system'}
                    onChange={() => setDisplayMode('system')}
                    disabled={!enabled}
                  />
                  System Overlay
                </label>
              </div>

              <div style={{ display: 'flex', gap: 18, marginBottom: 12 }}>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="radio"
                    name="watermarkMode"
                    value="system"
                    checked={watermarkIsSystemEnforced || mode === 'system'}
                    onChange={() => setMode('system')}
                    disabled={!enabled}
                  />
                  System Logo
                </label>
                {customWatermarkAllowed ? (
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="radio"
                      name="watermarkMode"
                      value="custom"
                      checked={mode === 'custom'}
                      onChange={() => setMode('custom')}
                      disabled={!enabled}
                    />
                    Custom Logo
                  </label>
                ) : null}
              </div>

              {(watermarkIsSystemEnforced || enabled) && mode === 'system' && (
                <div>
                  {systemLogos.length ? (
                    <div className="asset-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
                      {systemLogos.map((logo) => (
                        <button
                          type="button"
                          key={logo.id}
                          className="asset-card"
                          onClick={() => setSelectedSystemLogoId(logo.id)}
                          style={{
                            outline: logo.id === selectedSystemLogoId ? '2px solid #2563eb' : 'none',
                          }}
                        >
                          <img src={logo.url} alt={logo.label} />
                          <span>{logo.label}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p>No system logos were found in the configured system logo folder.</p>
                  )}
                </div>
              )}

              {customWatermarkAllowed && enabled && mode === 'custom' && (
                <div>
                  {customLogos.length ? (
                    <div className="asset-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
                      {customLogos.map((logo) => (
                        <button
                          type="button"
                          key={logo.id}
                          className="asset-card"
                          onClick={() => setSelectedCustomLogo(logo)}
                          style={{
                            outline:
                              Number(selectedCustomLogo?.id) === Number(logo.id)
                                ? '2px solid #2563eb'
                                : 'none',
                          }}
                        >
                          {logo.preview_api_url || logo.thumbnail_url ? (
                            <img src={logo.preview_api_url || logo.thumbnail_url} alt={logo.title || 'Logo'} />
                          ) : (
                            <div className="asset-placeholder">No preview</div>
                          )}
                          <span>{logo.title || `Logo #${logo.id}`}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p>
                      No custom logos uploaded yet. Upload one via Settings → Site Details → Upload Logo.
                    </p>
                  )}
                </div>
              )}

              <div style={{ marginTop: 14 }}>
                <button type="button" onClick={handleSave} disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save & Apply'}
                </button>
                <span style={{ marginLeft: 10, fontSize: 12, color: '#666' }}>
                  Applies to the currently selected device (if any).
                </span>
              </div>
            </div>

            <div style={{ width: 280 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Preview</div>
              <div
                style={{
                  width: 280,
                  height: 180,
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  background: '#fff',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Watermark preview"
                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                  />
                ) : (
                  <div style={{ color: '#6b7280' }}>{enabled ? 'Select a logo' : 'Disabled'}</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default WatermarkSettings;
